import { useRouter } from "@/lib/nav";
import { FormEvent, useState } from "react";

import Field from "@/components/field";
import SignaturePad from "@/components/signature-pad";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import {
  CHANGE_ORDER_CONSENT_LABEL,
  CHANGE_ORDER_DECIDE_FAILED_MESSAGE,
  CHANGE_ORDER_RECORD_NOTICE,
} from "@buildpy/core/modules/change-order/messages";
import { acceptChangeOrderByLink, rejectChangeOrderByLink } from "@/api/actions/change-order-share";

interface Props {
  token: string;
  changeOrderId: string;
  // 고객이 본 문서의 해시 — 그 사이 문서가 바뀌었으면 저장소가 막는다.
  docHash: string | null;
}

// 고객 링크 변경공사 결정 폼 — 이름 + 합의 체크 + 손서명으로 합의, 사유(선택)로 거절. 결과 화면은 새로고침하면 서버가 그린다.
const ChangeOrderAcceptForm = ({ token, changeOrderId, docHash }: Props) => {
  const router = useRouter();
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [pending, setPending] = useState(false);

  const decide = async (call: () => ReturnType<typeof acceptChangeOrderByLink>) => {
    if (pending || locked) return;
    setPending(true);
    setMessage(null);
    try {
      const result = await call();
      if (result.ok) {
        router.refresh();
        return;
      }
      setMessage(result.message);
      setLocked(Boolean(result.locked || result.stale));
      if (result.reverify || result.stale) router.refresh();
    } catch {
      setMessage(CHANGE_ORDER_DECIDE_FAILED_MESSAGE);
    } finally {
      setPending(false);
    }
  };

  const accept = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void decide(() => acceptChangeOrderByLink({ token, id: changeOrderId, name, agree, doc_hash: docHash, signature }));
  };

  const reject = () => {
    void decide(() => rejectChangeOrderByLink({ token, id: changeOrderId, name, reason }));
  };

  return (
    <SectionCard
      as="form"
      className="print-hide mx-auto flex w-full max-w-md flex-col gap-y-4 p-5"
      data-testid="change-order-accept-form"
      padding="none"
      onSubmit={accept}>
      <div className="flex flex-col gap-y-1">
        <h3 className="text-base font-semibold">변경 내용 합의</h3>
        <p className="text-sm text-muted-foreground">{CHANGE_ORDER_RECORD_NOTICE}</p>
      </div>

      <Field label="도급인 이름">
        <Input
          autoComplete="name"
          disabled={locked}
          inputSize="lg"
          maxLength={50}
          name="name"
          placeholder="이름을 직접 입력해 주세요"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      {rejecting ? (
        <Field label="거절 사유(선택)">
          <Textarea
            className="min-h-11 text-lg"
            disabled={locked}
            maxLength={1000}
            name="reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      ) : (
        <label className="flex items-start gap-x-2 text-sm">
          <Checkbox
            checked={agree}
            className="mt-0.5"
            disabled={locked}
            name="agree"
            onCheckedChange={(checked) => setAgree(checked === true)}
          />
          <span>{CHANGE_ORDER_CONSENT_LABEL}</span>
        </label>
      )}

      {/* 칸이 다시 그려지면(거절 → 돌아가기) 서명 칸이 처음에 null 을 알려 빈 서명으로 돌아간다. */}
      {!rejecting && <SignaturePad disabled={locked} label="도급인 서명" onChange={setSignature} />}

      {message && (
        <p className="text-sm text-destructive" role="alert">
          {message}
        </p>
      )}

      {rejecting ? (
        <div className="flex gap-2">
          <Button className="flex-1" disabled={pending} type="button" variant="outline" onClick={() => setRejecting(false)}>
            돌아가기
          </Button>
          <Button className="flex-1" disabled={pending || locked} type="button" variant="destructive" onClick={reject}>
            {pending ? "처리 중…" : "거절하기"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            className="w-full"
            disabled={pending || locked || !agree || name.trim().length === 0 || signature === null}
            type="submit"
            variant="primary">
            {pending ? "처리 중…" : "변경 내용에 합의합니다"}
          </Button>
          <Button className="w-full" disabled={pending || locked} type="button" variant="outline" onClick={() => setRejecting(true)}>
            거절
          </Button>
        </div>
      )}
    </SectionCard>
  );
};

export default ChangeOrderAcceptForm;
